import { createApp, defineAsyncComponent } from 'vue';

import '@/polyfills';

import 'sticky-sidebar/dist/sticky-sidebar';
import '@/filters';

/**
 * Routers
 */

import searchRouter from '@/vue-router/search-router';
import dashboardRouter from '@/vue-router/dashboard-router';
import registrationRouter from '@/vue-router/registration-router';

/**
 * Store
 */

import store from '@/vuex/index';
import storageManager from '@/storage-manager';

/**
 * Plugins
 */

import googleTagPlugin from '@/google-analytics';
import eventBus from '@/event-bus';
import langPlugin from '@/lang/index';
import { bugsnagClient } from '@/bugsnag';
import { axios } from '@/axios';

/**
 * Vanilla js replacements of jquery
 */
import '@/vanilla/mobile-menu';
import '@/vanilla/flash-messages';
import '@/vanilla/company-profile';
import '@/vanilla/search/global-autocomplete';
import '@/vanilla/search/mobile-search';
import '@/vanilla/higherin-transition-message';

/**
 * Common elements used accross the website
 */
import SocialIcons from '@/components/common/btn-social-share.vue';
import PageLoadingCover from '@/components/common/page-loading-cover.vue';
import AuthenticationModal from '@/components/modal/modal-types/authentication-modal.vue';
import SaveItemModal from '@/components/modal/modal-types/save-item-modal.vue';
import UserNotificationsWithAccountMenu from '@/components/navigation/user-notifications-with-account-menu.vue';
import ReAuthentication from '@/components/authentication/common/re-authentication-data-provider.vue';
import ReAuthenticationForm from '@/components/authentication/forms/re-authentication-form.vue';
import BaseAlert from '@/components/alerts/base-alert.vue';

/**
 * Redirect buttons/modals
 */
import CompanyRedirectButton from '@/components/common/company-redirect-button.vue';

/**
 * Registration related components
 */
import RegisterEmailOnly from '@/components/authentication/register-touch-points/register-email-only.vue';

/**
 * Navigation components
 */
// TODO

/**
 * Dashboard components
 */
import DashboardLayout from '@/components/dashboard/layout/index.vue';

/**
 * Claim Review Incentives
 */
import ClaimReviewIncentive from '@/components/incentives/redeem';
import reviewRouter from './vue-router/review-router';

/**
 * TED components
 */
import RegisterInterest from './components/register-interest/register-interest-index.vue';

/**
 * Authentication components
 */
import AuthLogin from './components/authentication/login-page/login-page-index.vue';

// Note here for using RMP Dev Tools Chrome Extension locally
if (RMP_ENVIRONMENT !== 'production') {
  window.vuexStore = store;
}

/**
 * Directives
 */
function addScrollDirective(appInstance) {
  appInstance.directive('scroll', {
    inserted(el, binding) {
      const f = (evt) => {
        if (binding.value(evt, el)) {
          window.removeEventListener('scroll', f);
        }
      };
      window.addEventListener('scroll', f);
    },
  });
}

function bindEnvValues(appInstance) {
  appInstance.config.globalProperties.RMP_CDN_URL = process.env.MIX_CDN_URL;
  appInstance.config.globalProperties.RMP_ENVIRONMENT = process.env.MIX_APP_ENV;
  appInstance.config.globalProperties.RMP_CLIENT_PORTAL_URL = process.env.MIX_RMP_CLIENT_PORTAL_URL;
}

function bindGlobalVueProps(appInstance) {
  appInstance.config.globalProperties.$bus = eventBus;
  appInstance.config.globalProperties.$gtag = googleTagPlugin;
  appInstance.config.globalProperties.$lang = langPlugin;
  appInstance.config.globalProperties.$bugsnag = bugsnagClient;
  appInstance.config.globalProperties.$axios = axios;

  bindEnvValues(appInstance);

  if (appInstance.config.globalProperties.RMP_ENVIRONMENT === 'production') {
    appInstance.use(bugsnagClient.getPlugin('vue'));
  }
}

/**
 *  Storage Manager
 */
// Clear old stored keys
storageManager.clearSessionOldKeys();

// TODO uncomment once saved item store implemented.
// On page load, if the user is logged in
//  - load the shortlisted items via ajax if not in session storage
// if not logged in, clear items in session storage
const sessionItems = storageManager.getSessionStorage({ key: 'saved-items|items' });

if (store.state.user.loggedIn === true) {
  if (sessionItems !== null && sessionItems.length !== 0) {
    store.commit('savedItems/setItemData', sessionItems);
  } else {
    store.dispatch('savedItems/getSavedItems');
  }
} else {
  storageManager.emptySessionStorage();
}

if (document.getElementById('vue-cms-hotjobs-carousel')) {
  const cmsCarousel = createApp({
    components: {
      'cms-hotjobs-carousel': defineAsyncComponent(() => import(/* webpackChunkName: "group-saved-items" */ './components/cms/cms-carousel.vue')),
    },
  });
  cmsCarousel.use(store);
  bindGlobalVueProps(cmsCarousel);
  cmsCarousel.mount('#vue-cms-hotjobs-carousel');
}

const articlesCarousel = document.querySelectorAll('#vue-cms-articles-carousel');
if (articlesCarousel !== null) {
  articlesCarousel.forEach((element) => {
    const articleCarousel = createApp({
      components: {
        'cms-articles-carousel': defineAsyncComponent(() => import(/* webpackChunkName: "group-cms-articles-carousel" */ './components/cms/cms-carousel-articles.vue')),
      },
    });

    articleCarousel.use(store);
    bindGlobalVueProps(articleCarousel);
    articleCarousel.mount(element);
  });
}


if (document.getElementById('vue-cms-reviews-carousel')) {
  const cmsCarousel = createApp({
    components: {
      'cms-reviews-carousel': defineAsyncComponent(() => import(/* webpackChunkName: "group-saved-items" */ './components/cms/cms-carousel.vue')),
    },
  });
  cmsCarousel.use(store);
  bindGlobalVueProps(cmsCarousel);
  cmsCarousel.mount('#vue-cms-reviews-carousel');
}

if (document.getElementById('vue-cms-case-studies-carousel')) {
  const cmsCarousel = createApp({
    components: {
      'cms-case-studies-carousel': defineAsyncComponent(() => import(/* webpackChunkName: "group-saved-items" */ './components/cms/cms-carousel.vue')),
    },
  });
  cmsCarousel.use(store);
  bindGlobalVueProps(cmsCarousel);
  cmsCarousel.mount('#vue-cms-case-studies-carousel');
}

const gtagTracking = document.querySelectorAll('#vue-cms-gtag-tracking');
if (gtagTracking !== null) {
  gtagTracking.forEach((element) => {
    const cmsGtagTracking = createApp({
      components: {
        'gtag-tracking': defineAsyncComponent(() => import(/* webpackChunkName: "group-gtag-tracking" */ './components/cms/gtag-tracking.vue')),
      },
    });

    cmsGtagTracking.use(store);
    bindGlobalVueProps(cmsGtagTracking);
    cmsGtagTracking.mount(element);
  });
}

// Global Alerts
if (document.getElementById('Teleport-alerts-destination')) {
  const alertsApp = createApp({
    components: {
      BaseAlert,
    },
  });
  alertsApp.use(store);
  bindGlobalVueProps(alertsApp);
  alertsApp.mount('#Teleport-alerts-destination');
}

// Global footer
if (document.getElementById('Teleport-footer-modals')) {
  const footerModalsApp = createApp({
    components: {
      AuthenticationModal,
      SaveItemModal,
    },
  });
  footerModalsApp.use(store);
  bindGlobalVueProps(footerModalsApp);
  footerModalsApp.mount('#Teleport-footer-modals');
}

if (document.getElementById('vue-claim-incentive')) {
  const incentives = createApp({
    components: {
      'vue-claim-incentive': defineAsyncComponent(() => import(/* webpackChunkName: "group-incentive" */ '@/components/incentives/redeem')),
    },
  });

  incentives.mount('#vue-claim-incentive');
}

/* eslint-disable no-new */
/**
 * TED app
 * @url /top-employers
 */
if (document.getElementById('vue-ted-app')) {
  const tedApp = createApp({
    components: {
      'top-table': defineAsyncComponent(() => import(/* webpackChunkName: "group-ted" */ '@/components/top-employers/index.vue')),
    },
  });

  tedApp.use(store);
  bindGlobalVueProps(tedApp);
  tedApp.mount('#vue-ted-app');
}

if (document.querySelectorAll('#review-process').length) {
  const reviewProcessApp = createApp({
    components: {
      'page-loading-cover': PageLoadingCover,
    },
  });
  reviewProcessApp.use(store);
  reviewProcessApp.use(reviewRouter);
  addScrollDirective(reviewProcessApp);
  bindGlobalVueProps(reviewProcessApp);
  reviewProcessApp.mount('#review-process');
}

if (document.querySelector('#vue-header')) {
  const headerApp = createApp({
    components: {
      UserNotificationsWithAccountMenu,
    },
  });

  headerApp.use(store);
  bindGlobalVueProps(headerApp);
  headerApp.mount('#vue-header');
}

if (document.getElementById('vue-social-icons')) {
  const socialIconsApp = createApp({
    components: {
      'social-icons': SocialIcons,
    },
  });
  bindGlobalVueProps(socialIconsApp);
  socialIconsApp.mount('#vue-social-icons');
}

// Used on company menus and job listings
if (document.getElementById('vue-company-menu-shortlist-icon')) {
  const saveItemButtonApp = createApp({
    components: {
      heart: defineAsyncComponent(() => import(/* webpackChunkName: "group-saved-items" */ './components/common/save-item-button.vue')),
    },
  });
  saveItemButtonApp.use(store);
  bindGlobalVueProps(saveItemButtonApp);
  saveItemButtonApp.mount('#vue-company-menu-shortlist-icon');
}

if (document.getElementById('vue-company-redirect')) {
  const companyRedirectApp = createApp({
    components: {
      'company-redirect-button': defineAsyncComponent(() => import(/* webpackChunkName: "group-company-page" */ './components/common/company-redirect-button.vue')),
    },
  });
  companyRedirectApp.use(store);
  bindGlobalVueProps(companyRedirectApp);
  companyRedirectApp.mount('#vue-company-redirect');
}

if (document.getElementById('vue-hot-jobs')) {
  const vueHotJobsApp = createApp({
    components: {
      heart: defineAsyncComponent(() => import(/* webpackChunkName: "group-saved-items" */ './components/common/save-item-button.vue')),
      'save-item-button': defineAsyncComponent(() => import(/* webpackChunkName: "group-saved-items" */ './components/common/save-item-button.vue')),
    },
  });
  vueHotJobsApp.use(store);
  bindGlobalVueProps(vueHotJobsApp);
  vueHotJobsApp.mount('#vue-hot-jobs');
}

if (document.getElementById('vue-related-reviews-content')) {
  const relatedReviewsApp = createApp({
    components: {
      heart: defineAsyncComponent(() => import(/* webpackChunkName: "group-saved-items" */ './components/common/save-item-button.vue')),
    },
  });
  relatedReviewsApp.use(store);
  bindGlobalVueProps(relatedReviewsApp);
  relatedReviewsApp.mount('#vue-related-reviews-content');
}

if (document.getElementById('vue-company-profile-items')) {
  const companyProfileItemsApp = createApp({
    components: {
      'save-item-button': defineAsyncComponent(() => import(/* webpackChunkName: "group-saved-items" */ './components/common/save-item-button.vue')),
    },
  });
  companyProfileItemsApp.use(store);
  bindGlobalVueProps(companyProfileItemsApp);
  companyProfileItemsApp.mount('#vue-company-profile-items');
}

if (document.getElementById('vue-job-page')) {
  const JobsPageApp = createApp({
    components: {
      'save-item-button': defineAsyncComponent(() => import(/* webpackChunkName: "group-saved-items" */ './components/common/save-item-button.vue')),
      RegisterInterestButton: defineAsyncComponent(() => import(/* webpackChunkName: "group-ryi" */ './components/common/register-interest-button.vue')),
      JobRedirectButton: defineAsyncComponent(() => import(/* webpackChunkName: "group-job" */ './components/common/job-redirect-button.vue')),
    },
  });
  JobsPageApp.use(store);
  bindGlobalVueProps(JobsPageApp);
  JobsPageApp.mount('#vue-job-page');
}

if (document.getElementById('vue-job-apply-description')) {
  const JobDescriptionApp = createApp({
    components: {
      'save-item-button': defineAsyncComponent(() => import(/* webpackChunkName: "group-saved-items" */ './components/common/save-item-button.vue')),
      RegisterInterestButton: defineAsyncComponent(() => import(/* webpackChunkName: "group-ryi" */ './components/common/register-interest-button.vue')),
      JobRedirectButton: defineAsyncComponent(() => import(/* webpackChunkName: "group-job" */ './components/common/job-redirect-button.vue')),
    },
  });
  JobDescriptionApp.use(store);
  bindGlobalVueProps(JobDescriptionApp);
  JobDescriptionApp.mount('#vue-job-apply-description');
}

/* This will be removed when we move to the core-pages */

if (document.getElementById('vue-job-footer-actions')) {
  const footerActionsApp = createApp({
    components: {
      RegisterInterest,
      JobRedirectButton: defineAsyncComponent(() => import(/* webpackChunkName: "group-job" */ './components/common/job-redirect-button.vue')),
      heart: defineAsyncComponent(() => import(/* webpackChunkName: "group-saved-items" */ './components/common/save-item-button.vue')),
    },
  });
  footerActionsApp.use(store);
  bindGlobalVueProps(footerActionsApp);
  footerActionsApp.mount('#vue-job-footer-actions');
}

if (document.getElementById('vue-register-interest-form')) {
  const registerInterestApp = createApp({
    components: {
      RegisterInterest,
    },
  });
  registerInterestApp.use(store);
  bindGlobalVueProps(registerInterestApp);
  registerInterestApp.mount('#vue-register-interest-form');
}

if (document.getElementById('vue-company-page')) {
  const companyPagesApp = createApp({
    components: {
      CompanyRedirectButton,
      'save-item-button': defineAsyncComponent(() => import(/* webpackChunkName: "group-saved-items" */ './components/common/save-item-button.vue')),
    },
  });
  companyPagesApp.use(store);
  bindGlobalVueProps(companyPagesApp);
  companyPagesApp.mount('#vue-company-page');
}

if (document.getElementById('vue-search-app')) {
  const searchApp = createApp({
    components: {
      'search-index': defineAsyncComponent(() => import(/* webpackChunkName: "group-search" */ '@/components/search/search-index.vue')),
    },
  });
  searchApp.use(store);
  if (window.history.pushState) {
    searchApp.use(searchRouter);
  }
  bindGlobalVueProps(searchApp);
  searchApp.mount('#vue-search-app');
}

/**
 * Main site Login
 * @url /login
 */
if (document.getElementById('vue-login-form')) {
  const loginFormApp = createApp({
    components: {
      'auth-login': AuthLogin,
    },
  });
  loginFormApp.use(store);
  bindGlobalVueProps(loginFormApp);
  loginFormApp.mount('#vue-login-form');
}

/**
 * Main site Register App and router
 * @url /register
 */
if (document.getElementById('vue-auth-registration-app')) {
  registrationRouter.beforeEach((to, from, next) => {
    window.gtag.send({
      event: document.title,
      action: 'page_view',
      category: window.location.pathname,
      location: window.location.href,
    });
    next();
  });

  const registrationAuthApp = createApp({
    components: {
      'auth-registration': defineAsyncComponent(() => import(/* webpackChunkName: "group-registration" */ './components/authentication/register-page/register-page-index.vue')),
    },
  });
  registrationAuthApp.use(store);
  registrationAuthApp.use(registrationRouter);
  bindGlobalVueProps(registrationAuthApp);
  registrationAuthApp.mount('#vue-auth-registration-app');
}

// Footer signup form
if (document.getElementById('vue-footer-signup-form')) {
  const footerSignUpApp = createApp({
    components: {
      'footer-signup-form': defineAsyncComponent(() => import(/* webpackChunkName: "group-footer" */'@/components/authentication/register-touch-points/footer-register-email-only.vue')),
    },
  });
  footerSignUpApp.use(store);
  bindGlobalVueProps(footerSignUpApp);
  footerSignUpApp.mount('#vue-footer-signup-form');
}

/**
 * Incremental data forms
 *
 * @link https://rmpenterprise.atlassian.net/wiki/display/RATE/Ajax+Registration+Blocks+Which+Require+Email+Only Documentation
 */
const $incrementalDataForms = document.querySelectorAll('.vue-incremental-data-signups');

if ($incrementalDataForms !== null) {
  $incrementalDataForms.forEach((element) => {
    const incrementalDataApp = createApp({
      components: {
        'register-email-only': RegisterEmailOnly,
        're-authentication': ReAuthentication,
        're-authentication-form': ReAuthenticationForm,
      },
    });
    incrementalDataApp.use(store);
    bindGlobalVueProps(incrementalDataApp);
    incrementalDataApp.mount(element);
  });
}

/**
 * Dashboard app and router
 * @url /dashboard
 * @desc router controlled pages in dashboard
 */
if (document.getElementById('vue-dashboard-app')) {
  dashboardRouter.beforeEach((to, from, next) => {
    window.gtag.send({
      event: document.title,
      action: 'page_view',
      category: window.location.pathname,
      location: window.location.href,
    });
    next();
  });
  const dashboardApp = createApp({
    components: {
      'dashboard-layout': DashboardLayout,
    },
  });
  dashboardApp.use(store);
  bindGlobalVueProps(dashboardApp);
  dashboardApp.use(dashboardRouter);
  dashboardApp.mount('#vue-dashboard-app');
}
